import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { API } from "../config/api"
import { useAuth } from "../context/AuthContext"
import { THEME } from "../config/theme"
import PasswordRequirements from "../components/PasswordRequirements"
import { isPasswordValid } from "../utils/password"

const ChangePasswordPage: React.FC = () => {
  const { token, adoptToken } = useAuth()
  const navigate = useNavigate()

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!isPasswordValid(newPassword)) {
      setError("Password must meet all requirements")
      return
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match")
      return
    }

    setLoading(true)
    try {
      const resp = await fetch(
        `${API.baseUrl}${API.endpoints.changePassword}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword,
          }),
        }
      )
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ detail: "Failed to change password" }))
        setError(data.detail || "Failed to change password")
        return
      }
      // The old login stopped working with the old password; keep the new one.
      const changed = await resp.json().catch(() => ({}))
      if (typeof changed.access_token === "string") adoptToken(changed.access_token)
      navigate("/dashboard")
    } catch {
      setError("Failed to change password")
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "44px",
    border: `0.5px solid ${THEME.colors.neutral.light.border}`,
    borderRadius: THEME.borderRadius.md,
    padding: "0 12px",
    fontSize: "15px",
    fontFamily: THEME.fonts.primary,
    boxSizing: "border-box",
    marginBottom: "16px",
    outline: "none",
  }

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: THEME.colors.neutral.light.textPrimary,
    marginBottom: "6px",
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: THEME.colors.brandGradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: THEME.fonts.primary,
      }}
    >
      <div
        style={{
          width: "400px",
          background: THEME.colors.neutral.light.surface,
          border: `0.5px solid ${THEME.colors.neutral.light.border}`,
          borderRadius: THEME.borderRadius.lg,
          padding: "40px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            fontSize: "24px",
            fontWeight: 600,
            color: THEME.colors.neutral.light.textPrimary,
            textAlign: "center",
            marginBottom: "8px",
          }}
        >
          Set your password
        </div>
        <div
          style={{
            fontSize: "14px",
            color: THEME.colors.neutral.light.textSecondary,
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          You must set a new password before continuing.
        </div>

        <form onSubmit={handleSubmit}>
          <label style={labelStyle} htmlFor="current">Current password</label>
          <input
            id="current"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={inputStyle}
            autoComplete="current-password"
            required
          />

          <label style={labelStyle} htmlFor="new">New password</label>
          <input
            id="new"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={inputStyle}
            autoComplete="new-password"
            minLength={8}
            required
          />

          <label style={labelStyle} htmlFor="confirm">Confirm new password</label>
          <input
            id="confirm"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={inputStyle}
            autoComplete="new-password"
            required
          />

          <PasswordRequirements password={newPassword} />

          {(() => {
            const canSubmit =
              !loading &&
              isPasswordValid(newPassword) &&
              newPassword === confirmPassword
            return (
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  width: "100%",
                  height: "44px",
                  background: THEME.colors.navy,
                  color: THEME.colors.white,
                  fontWeight: 500,
                  fontSize: "14px",
                  border: "none",
                  borderRadius: THEME.borderRadius.md,
                  marginTop: "8px",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  opacity: canSubmit ? 1 : 0.7,
                  fontFamily: THEME.fonts.primary,
                }}
              >
                {loading ? "Saving..." : "Set password"}
              </button>
            )
          })()}

          {error && (
            <div
              style={{
                background: "rgba(220, 38, 38, 0.08)",
                color: THEME.colors.semantic.danger,
                border: "0.5px solid rgba(220, 38, 38, 0.3)",
                padding: "10px 12px",
                borderRadius: THEME.borderRadius.md,
                marginTop: "12px",
                fontSize: "13px",
              }}
            >
              {error}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ChangePasswordPage
